import { Stack } from "@ucchub/ui";
import Paragraph from "@vfuk/core-paragraph/";
import Divider from "@vfuk/core-divider";
import Heading from "@vfuk/core-heading";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import SimpleGrid from "@vfuk/core-simple-grid";
import Spacing from "@vfuk/core-spacing";
import Button from "@vfuk/core-button/";
import AccountConfigFormInput from "./AccountConfigFormInput";
import InlineNotification from "@vfuk/core-in-line-notification";
import type { InLineNotificationTheme } from "@vfuk/core-in-line-notification";
import type {
  AccountConfigFormType,
  AccountConfigAddRequest,
} from "./AccountConfig.types";
import StateNotification from "@vfuk/core-state-notification";

const EMAIL_MAX_LENGTH = 100;
const PLATFORM_ID_MAX_LENGTH = 10;

export const EMAIL_VALIDATION_REG_EXP =
  /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i;

const PLATFORM_ID_REG_EXP = /^[A-Za-z0-9.,!?;:'"(){}[\]\-_+=*&^%$#@~/\\]*$/i;

export type AccountConfigFormProps = {
  onSubmit: (data: AccountConfigAddRequest) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  error?: string;
  accountConfigToEdit?: AccountConfigFormType;
};

const AccountConfigForm = ({
  onSubmit,
  onCancel,
  isSubmitting,
  error,
  accountConfigToEdit,
}: AccountConfigFormProps) => {
  const { t } = useTranslation();

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty, isValid },
  } = useForm<AccountConfigFormType>({
    shouldFocusError: false,
    mode: "onTouched",
    defaultValues: accountConfigToEdit,
  });

  const disabledState = isSubmitting ? "disabled" : undefined;
  const saveBtnDisabledState =
    !isValid || !isDirty ? "disabled" : disabledState;

  const handleOnSubmit = (data: AccountConfigFormType) => {
    if (!saveBtnDisabledState) {
      onSubmit(data);
    }
  };

  return (
    <>
      <Heading
        level={4}
        weight={3}
        noMargin
        text={t("core:accountConfig:account_config") || undefined}
      />
      <Paragraph size={2}>{t("core:accountConfig:form_subtitle")}</Paragraph>
      {error && (
        <Spacing spacingLevel={{ top: 4 }}>
          <StateNotification state="error">
            <Paragraph>{error}</Paragraph>
          </StateNotification>
        </Spacing>
      )}
      <Spacing spacingLevel={{ top: 6, bottom: 7 }}>
        <Divider appearance="secondary" />
      </Spacing>
      <form onSubmit={handleSubmit(handleOnSubmit)}>
        <SimpleGrid
          columns={{
            sm: 2,
            lg: 3,
          }}
          spacing={10}
        >
          <AccountConfigFormInput
            name="commercialManagerEmail"
            control={control}
            label={t("core:accountConfig:commercial_manager_email")}
            error={errors.commercialManagerEmail?.message || undefined}
            state={errors.commercialManagerEmail ? "error" : disabledState}
            rules={{
              required: {
                value: true,
                message: t("core:accountConfig:validation:required", {
                  name: t("core:accountConfig:commercial_manager_email"),
                }),
              },
              maxLength: {
                value: EMAIL_MAX_LENGTH,
                message: t("core:accountConfig:validation:max_length", {
                  max: EMAIL_MAX_LENGTH,
                }),
              },
              pattern: {
                value: EMAIL_VALIDATION_REG_EXP,
                message: t("core:accountConfig:validation:email"),
              },
            }}
          />
          <AccountConfigFormInput
            name="accountManagerEmail"
            control={control}
            label={t("core:accountConfig:account_manager_email")}
            error={errors.accountManagerEmail?.message || undefined}
            state={errors.accountManagerEmail ? "error" : disabledState}
            rules={{
              required: {
                value: true,
                message: t("core:accountConfig:validation:required", {
                  name: t("core:accountConfig:account_manager_email"),
                }),
              },
              maxLength: {
                value: EMAIL_MAX_LENGTH,
                message: t("core:accountConfig:validation:max_length", {
                  max: EMAIL_MAX_LENGTH,
                }),
              },
              pattern: {
                value: EMAIL_VALIDATION_REG_EXP,
                message: t("core:accountConfig:validation:email"),
              },
            }}
          />
        </SimpleGrid>
        <SimpleGrid
          columns={{
            sm: 2,
            lg: 3,
          }}
          spacing={10}
        >
          <AccountConfigFormInput
            name="voicePlatformId"
            control={control}
            label={t("core:accountConfig:voice_platform_id")}
            error={errors.voicePlatformId?.message || undefined}
            state={errors.voicePlatformId ? "error" : disabledState}
            rules={{
              required: {
                value: true,
                message: t("core:accountConfig:validation:required", {
                  name: t("core:accountConfig:voice_platform_id"),
                }),
              },
              maxLength: {
                value: PLATFORM_ID_MAX_LENGTH,
                message: t("core:accountConfig:validation:max_length", {
                  max: PLATFORM_ID_MAX_LENGTH,
                }),
              },
              pattern: {
                value: PLATFORM_ID_REG_EXP,
                message: t("core:accountConfig:validation:id"),
              },
            }}
          />
          <AccountConfigFormInput
            name="messagingPlatformId"
            control={control}
            error={errors.messagingPlatformId?.message || undefined}
            state={errors.messagingPlatformId ? "error" : disabledState}
            label={t("core:accountConfig:messaging_platform_id")}
            rules={{
              maxLength: {
                value: PLATFORM_ID_MAX_LENGTH,
                message: t("core:accountConfig:validation:max_length", {
                  max: PLATFORM_ID_MAX_LENGTH,
                }),
              },
              pattern: {
                value: PLATFORM_ID_REG_EXP,
                message: t("core:accountConfig:validation:id"),
              },
            }}
          />
        </SimpleGrid>
        <Spacing spacingLevel={{ top: 7, bottom: 5 }}>
          <Divider appearance="secondary" />
        </Spacing>
        <Stack justifyContent="space-between">
          <InlineNotification
            text={t("core:accountConfig:form_warning")}
            state="warn"
            localTheme={{
              appearance: {
                primary: {
                  backgroundColor: "white",
                },
              } as InLineNotificationTheme["appearance"],
            }}
          />
          <Stack columnGap="1rem" justifyContent="flex-end">
            <Button
              text={t("core:accountConfig:cancel")}
              appearance="alt1"
              onClick={onCancel}
              state={disabledState}
            />
            <Button
              onClick={() => null}
              text={t("core:accountConfig:submit")}
              appearance="primary"
              htmlAttributes={{ type: "submit" }}
              state={saveBtnDisabledState}
            />
          </Stack>
        </Stack>
      </form>
    </>
  );
};

export default AccountConfigForm;
